import { useCallback, useMemo, useState } from "react";
import { useAppSelector } from "@/app/appStore";
import { useGondolaChangeGroupsData } from "@/features/gondola-change-groups/hooks";
import { selectCurrentCamera } from "@/entities/annotation/model/slices/slice";
import { useGetTrackPinsQuery } from "@/shared/api";
import {
  selectTrackAvatarBindings,
  selectVisualizeTrackProjections,
} from "@/entities/gondola-change-groups";
import { processTrackPinsData } from "@/features/gondola-change-groups/lib/helpers";
import { PlaybackState, useMediaState } from "@tezzro/player";
import toast from "react-hot-toast";

const imageDimensions = {
  width: 1920,
  height: 1080,
};

const TRACK_PINS_STATUS_TYPES = {
  LOADING: "loading",
  NO_DATA: "no_data",
  ALL_FILTERED_OUT: "all_filtered_out",
  SOME_FILTERED_OUT: "some_filtered_out",
  ALL_VISIBLE: "all_visible",
} as const;

const useTrackPins = () => {
  const canvasDimensions = useAppSelector(
    (state) => state.annotation.canvasDimensions
  );
  const currentCamera = useAppSelector(selectCurrentCamera);
  const lastStoppedTime = useMediaState("lastStoppedTime");
  const visualizeTrackProjections = useAppSelector(
    selectVisualizeTrackProjections
  );
  const trackAvatarBindings = useAppSelector(selectTrackAvatarBindings);
  const playbackState = useMediaState("playbackState");

  const { selectedSkuSectionGroup } = useGondolaChangeGroupsData();

  const [copiedTimestamp, setCopiedTimestamp] = useState<string | null>(null);

  const trackPinsQueryParams = useMemo(
    () => ({
      track_ids: selectedSkuSectionGroup?.track_ids_in_occlusion || [],
      ...(lastStoppedTime && {
        time: new Date(lastStoppedTime).toISOString(),
      }),
      camera_local_id: currentCamera,
    }),
    [
      selectedSkuSectionGroup?.track_ids_in_occlusion,
      currentCamera,
      lastStoppedTime,
    ]
  );

  const shouldSkipTrackPinsQuery = useMemo(
    () => !visualizeTrackProjections || !lastStoppedTime || !currentCamera,
    [visualizeTrackProjections, currentCamera, lastStoppedTime]
  );

  const {
    data: trackPinsData,
    isLoading: trackPinsLoading,
    isFetching: trackPinsFetching,
  } = useGetTrackPinsQuery(trackPinsQueryParams, {
    skip: shouldSkipTrackPinsQuery,
  });

  const displayTrackPins = useMemo(() => {
    const isValidContext =
      visualizeTrackProjections && !!trackPinsData && !!currentCamera;

    const isCameraDataValid =
      trackPinsData &&
      String(trackPinsData?.camera_id) === currentCamera &&
      trackPinsQueryParams.camera_local_id === currentCamera;

    if (!isCameraDataValid) {
      return [];
    }

    const pins = processTrackPinsData(
      trackPinsData,
      trackAvatarBindings,
      imageDimensions,
      currentCamera,
      isValidContext
    );

    return pins;
  }, [
    visualizeTrackProjections,
    trackPinsData,
    trackAvatarBindings,
    currentCamera,
    trackPinsQueryParams,
  ]);

  const positionedTrackPins = useMemo(() => {
    if (displayTrackPins.length === 0) return [];

    const scaleX = canvasDimensions.width / imageDimensions.width;
    const scaleY = canvasDimensions.height / imageDimensions.height;

    return displayTrackPins.map((pin) => ({
      ...pin,
      x: pin.x * scaleX,
      y: pin.y * scaleY,
    }));
  }, [canvasDimensions, displayTrackPins]);

  const copyTrackPinsTimestamp = useCallback((timestamp: string) => {
    navigator.clipboard.writeText(timestamp).then(() => {
      setCopiedTimestamp(timestamp);
      setTimeout(() => setCopiedTimestamp(null), 2000);
      toast.success("Copied to clipboard");
    });
  }, []);

  const trackPinsStatus = useMemo(() => {
    if (!visualizeTrackProjections) {
      return null;
    }

    const isLoading = trackPinsLoading || trackPinsFetching;
    const isValidPlaybackState = [
      PlaybackState.Paused,
      PlaybackState.Idle,
    ].includes(playbackState);

    if (!trackPinsData && isLoading && isValidPlaybackState) {
      return {
        type: TRACK_PINS_STATUS_TYPES.LOADING,
        message: "Loading track projections...",
        showSpinner: false,
        timestamp: null,
      };
    }

    if (!trackPinsData) {
      return null;
    }

    const result = trackPinsData.result;
    const timestamp = trackPinsData.time;
    const formattedTime = timestamp
      ? new Date(timestamp).toISOString()
      : "unknown time";

    const showSpinner = isLoading && isValidPlaybackState;

    if (
      !result ||
      !result.pin_points_2d?.length ||
      !result.track_ids?.length ||
      !result.closest_times_per_track_id?.length
    ) {
      return {
        type: TRACK_PINS_STATUS_TYPES.NO_DATA,
        message: `No track projections data available for time: ${formattedTime}`,
        showSpinner,
        timestamp,
      };
    }

    const totalTracks = result.track_ids.length;
    const visibleTracks = displayTrackPins.length;

    if (totalTracks > 0 && visibleTracks === 0) {
      return {
        type: TRACK_PINS_STATUS_TYPES.ALL_FILTERED_OUT,
        message: `All ${totalTracks} track projections are outside camera viewport at ${formattedTime}`,
        showSpinner,
        timestamp,
      };
    }

    if (totalTracks > visibleTracks && visibleTracks > 0) {
      return {
        type: TRACK_PINS_STATUS_TYPES.SOME_FILTERED_OUT,
        message: `${visibleTracks} of ${totalTracks} track projections visible at ${formattedTime}`,
        showSpinner,
        timestamp,
      };
    }

    if (totalTracks === visibleTracks && visibleTracks > 0) {
      return {
        type: TRACK_PINS_STATUS_TYPES.ALL_VISIBLE,
        message: `All ${totalTracks} track projections visible at ${formattedTime}`,
        showSpinner,
        timestamp,
      };
    }

    return null;
  }, [
    visualizeTrackProjections,
    trackPinsData,
    displayTrackPins,
    trackPinsLoading,
    trackPinsFetching,
    playbackState,
  ]);

  return {
    trackPinsLoading: trackPinsLoading || trackPinsFetching,
    positionedTrackPins,
    trackPinsStatus,
    copiedTimestamp,
    copyTrackPinsTimestamp,
  };
};

export default useTrackPins;
